import { Box, Typography, Button, TextField, Grid } from "@mui/material";
import { motion } from "framer-motion";
import { MailOutline, GitHub, LinkedIn, Instagram } from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";
import { useForm } from 'react-hook-form';
import api from '../../utils/api';
import { useRef } from 'react';
import { useNotification } from '../shared/NotificationProvider';

const ContactSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const formRef = useRef(null);
  const notify = useNotification();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const response = await api.post('/contact', data);
      notify.success(
        response?.data?.message ||
          '✅ Message sent! Our team at FindMyRoommates will reach out shortly.'
      );
      reset();
    } catch (error) {
      notify.error(
        error?.response?.data?.message ||
          '❌ Failed to send your message. Please try again later.'
      );
    }
  };

  const onError = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#001d3d" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#001d3d" },
    },
    "& .MuiInputLabel-root": { color: "#616161" },
  };

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 4 }, backgroundColor: "#fff" }}>
      <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: "4px",
                  backgroundColor: "#001d3d",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontWeight: 800,
                    mb: 3,
                    fontSize: { xs: "2rem", md: "3rem" },
                    background: "linear-gradient(45deg, #fff 0%, #90caf9 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Get In Touch
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: "rgba(255,255,255,0.85)", fontSize: { xs: "1rem", md: "1.1rem" } }}>
                  Have questions or feedback? We'd love to hear from you! Reach out through our contact form or connect with us on social media.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <MailOutline sx={{ mr: 2, color: "#fff" }} />
                    <Typography variant="body1" sx={{ color: "#fff", fontSize: "0.95rem" }}>services@findmyroommates.in</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mt: 3 }}>
                  <Button variant="contained" size={isMobile ? "medium" : "large"} startIcon={<GitHub />} href="https://github.com/findmyroommates" target="_blank" fullWidth={isMobile} sx={{ backgroundColor: "#fff", color: "#212121", borderRadius: "4px", px: 3, "&:hover": { backgroundColor: "#e0e0e0" } }}>GitHub</Button>
                  <Button variant="outlined" size={isMobile ? "medium" : "large"} startIcon={<LinkedIn />} href="#" target="_blank" fullWidth={isMobile} sx={{ borderColor: "rgba(255,255,255,0.5)", color: "#fff", borderRadius: "4px", px: 3, "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.08)" } }}>LinkedIn</Button>
                  <Button variant="outlined" size={isMobile ? "medium" : "large"} startIcon={<Instagram />} href="#" target="_blank" fullWidth={isMobile} sx={{ borderColor: "rgba(255,255,255,0.5)", color: "#fff", borderRadius: "4px", px: 3, "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.08)" } }}>Instagram</Button>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <Box
                ref={formRef}
                component="form"
                onSubmit={handleSubmit(onSubmit, onError)}
                sx={{
                  p: { xs: 2, md: 4 },
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              >
                <TextField fullWidth label="Your Name *" {...register("name", { required: "Name is required" })} error={!!errors.name} helperText={errors.name?.message} margin="normal" size="small" sx={inputSx} />
                <TextField fullWidth label="Email Address *" {...register("email", { required: "Email is required", pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email address" } })} error={!!errors.email} helperText={errors.email?.message} margin="normal" size="small" type="email" sx={inputSx} />
                <TextField fullWidth label="Subject" {...register("subject")} margin="normal" size="small" sx={inputSx} />
                <TextField fullWidth label="Message *" {...register("message", { required: "Message is required" })} error={!!errors.message} helperText={errors.message?.message} margin="normal" multiline rows={4} size="small" sx={inputSx} />
                <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting} sx={{ mt: 3, backgroundColor: "#001d3d", color: "#fff", py: 1.5, borderRadius: "4px", fontWeight: 600, fontSize: { xs: "0.875rem", md: "1rem" }, "&:hover": { backgroundColor: "#002d5a" } }}>
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ContactSection;
